import type { ProductPackItem, ProductPackChoiceGroup } from '../../types/packs';

interface PackItemsListProps {
  items: ProductPackItem[];
  choiceGroups?: ProductPackChoiceGroup[];
  isExpanded: boolean;
  onToggle: () => void;
}

function formatPrice(price: number): string {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
}

function ItemRow({ item, isLast, isFirst }: { item: ProductPackItem; isLast: boolean; isFirst: boolean }) {
  return (
    <div
      className={`flex items-start justify-between py-2 ${!isLast ? 'border-b border-gray-100' : ''} ${isFirst ? 'pt-0' : ''} ${isLast ? 'pb-0' : ''}`}
    >
      <div className="min-w-0 flex-1">
        <span className="mr-1.5 font-semibold text-green-600">{item.quantity}x</span>
        <span className="text-[13px] text-gray-800">{item.product_title}</span>
        {item.format_name && (
          <span className="ml-1 text-xs text-gray-400">({item.format_name})</span>
        )}
      </div>
      <span className="ml-3 whitespace-nowrap text-[13px] text-gray-500">
        {formatPrice(item.subtotal)}
      </span>
    </div>
  );
}

export function PackItemsList({ items, choiceGroups, isExpanded, onToggle }: PackItemsListProps) {
  const totalItems = items.length + (choiceGroups?.reduce((sum, g) => sum + g.items.length, 0) ?? 0);
  const hasChoiceGroups = choiceGroups && choiceGroups.length > 0;

  return (
    <div className="mt-3 border-t border-gray-200 pt-3">
      <button
        className="flex w-full items-center bg-transparent p-0 text-sm text-gray-500 hover:text-green-600"
        onClick={onToggle}
        type="button"
      >
        <span className="mr-2 w-4 text-center">
          <i className={`fa fa-chevron-${isExpanded ? 'up' : 'down'}`} />
        </span>
        <span className="flex-1 text-left">
          {isExpanded ? 'Ocultar' : 'Ver'} {totalItems} productos
        </span>
      </button>

      {isExpanded && (
        <div className="mt-3 rounded-md bg-gray-50 p-3">
          {items.map((item, index) => (
            <ItemRow
              key={`${item.product_id}-${item.product_format_id}`}
              item={item}
              isFirst={index === 0}
              isLast={!hasChoiceGroups && index === items.length - 1}
            />
          ))}

          {hasChoiceGroups && choiceGroups.map((group) => (
            <div key={group.id} className="mt-2 border-t border-gray-200 pt-2">
              <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-gray-500">
                <i className={`fa fa-${group.multiple ? 'check-square-o' : 'dot-circle-o'} text-gray-400`} />
                {group.label}
              </div>
              {group.items.map((item, idx) => (
                <div key={`${item.product_id}-${item.product_format_id}`} className="pl-4">
                  <ItemRow item={item} isFirst={idx === 0} isLast={idx === group.items.length - 1} />
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
